import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BusinessNotificationRecord } from "@graftus/schema";
import { useProfile } from "@/context/ProfileContext";
import { useTRPC } from "@/trpc/client";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Relative age, coarse on purpose — the report is daily, so minute precision
 * would be false precision.
 */
function ageKey(
  iso: string | null,
): { key: string; count: number } | null {
  if (!iso) return null;
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return { key: "notifications.justNow", count: 0 };
  if (hours < 24) return { key: "notifications.hoursAgo", count: hours };
  return { key: "notifications.daysAgo", count: Math.floor(hours / 24) };
}

/**
 * Notification centre. Shows the morning "att uppmärksamma" report for members
 * who have the in-app channel enabled; the same content also goes out by email
 * unless they turned that off.
 */
export function NotificationBell({ className }: { className?: string }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { businessProfile } = useProfile();
  const businessId = businessProfile?.uid ?? "";
  const [open, setOpen] = useState(false);

  const listQuery = useQuery(
    trpc.notifications.list.queryOptions(
      { businessId, limit: 20, unreadOnly: false },
      { enabled: Boolean(businessId) },
    ),
  );

  // The badge reads an authoritative server count, not the length of the loaded
  // page: unread items can sit past the 20 newest (or be outnumbered by read
  // ones inside it), so deriving the count from `notifications` would undercount.
  const unreadCountQuery = useQuery(
    trpc.notifications.unreadCount.queryOptions(
      { businessId },
      { enabled: Boolean(businessId) },
    ),
  );

  const markRead = useMutation({
    ...trpc.notifications.markRead.mutationOptions(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [["notifications"]] }),
  });
  const markAllRead = useMutation({
    ...trpc.notifications.markAllRead.mutationOptions(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [["notifications"]] }),
  });

  const notifications = (listQuery.data ?? []) as BusinessNotificationRecord[];
  const unreadCount = unreadCountQuery.data?.count ?? 0;

  if (!businessId) return null;

  function handleOpen(notification: BusinessNotificationRecord) {
    if (notification.readAt == null) {
      markRead.mutate({ businessId, notificationId: notification.id });
    }
    setOpen(false);
    if (notification.link) navigate(notification.link);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative", className)}
          aria-label={t("notifications.title")}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-primary-foreground"
              aria-label={t("notifications.unreadCount", { count: unreadCount })}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">{t("notifications.title")}</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={() => markAllRead.mutate({ businessId })}
            >
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t("notifications.empty")}
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto">
            {notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => handleOpen(n)}
                  className={cn(
                    "flex w-full flex-col gap-0.5 border-b px-3 py-2.5 text-left last:border-0 hover:bg-muted/50",
                    n.readAt == null && "bg-primary/5",
                  )}
                >
                  <div className="flex items-start gap-2">
                    {n.readAt == null && (
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    )}
                    <span className="text-sm font-medium">{n.title}</span>
                  </div>
                  {n.body && (
                    <span className="text-xs text-muted-foreground">
                      {n.body}
                    </span>
                  )}
                  {(() => {
                    const age = ageKey(n.createdAt);
                    return age ? (
                      <span className="text-[11px] text-muted-foreground/70">
                        {t(age.key, { count: age.count })}
                      </span>
                    ) : null;
                  })()}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
